import { Action, createReducer, on } from "@ngrx/store";

import * as ProductActionTypes from "../../actions/products/product.actions";
import * as PartiesActionTypes from "../../actions/parties/parties.actions";

import { ColumnSorterInfo } from "src/app/models/custom-table/custom-table.model";
import { CategoryDTO } from "src/app/models/Categories/categoryDTO";
import { PropertyDTO } from "src/app/models/Properties/propertyDTO";
import { SiteProductDTO } from "src/app/models/Sites/siteProductDTO";
import { BarcodeTypeDTO, ProductTypeDTO } from "src/app/models/Products/productDTO";

export interface ProductState {
  isLoading: boolean;
  created: boolean;
  product: any;
  productList: any[];
  productExcelList: any[];
  productTotal: number;
  productTableConfig: ColumnSorterInfo[];
  familyProducts: any[];
  groupProducts: any[];
  unitProducts: any[];
  productTypes: ProductTypeDTO[];
  warehouseProducts: any[];
  categories: CategoryDTO[];
  properties: PropertyDTO[];
  siteList: SiteProductDTO[];
  barcodeTypes: BarcodeTypeDTO[];
}

export const initialState: ProductState = {
  isLoading: false,
  created: false,
  product: null,
  productExcelList: null,
  productList: null,
  productTotal: null,
  productTableConfig: null,
  familyProducts: null,
  groupProducts: null,
  unitProducts: null,
  productTypes: null,
  warehouseProducts: null,
  categories: null,
  properties: null,
  siteList: null,
  barcodeTypes: null,
};

const productReducer = createReducer(
  initialState,
  on(ProductActionTypes.resetProductsState, () => initialState),
  on(
    ProductActionTypes.getProductsList,
    ProductActionTypes.getProductsExcelList,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
        isLoading: true,
      } as ProductState)
  ),
  on(ProductActionTypes.loadProductsList, (state, { response }) => {
    return {
      ...state,
      productList: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
      productTotal: (response as any)?.result?.size
        ? (response as any)?.result?.size
        : 0,
    };
  }),
  on(
    ProductActionTypes.getFamilyProductsList,
    ProductActionTypes.getGroupProductsList,
    ProductActionTypes.getUnitProductsList,
    (state, { pageReq }) =>
      ({
        ...state,
        error: null,
        pageReq: pageReq,
        isLoading: true,
      } as ProductState)
  ),
  on(ProductActionTypes.loadFamilyProductsList, (state, { response }) => {
    return {
      ...state,
      familyProducts: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(ProductActionTypes.loadGroupProductsList, (state, { response }) => {
    return {
      ...state,
      groupProducts: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(ProductActionTypes.loadUnitProductsList, (state, { response }) => {
    return {
      ...state,
      unitProducts: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(ProductActionTypes.loadProductTypesList, (state, { response }) => {
    return {
      ...state,
      productTypes: (response as any)?.result
        ? (response as any)?.result
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(ProductActionTypes.loadProductsExcelList, (state, { response }) => {
    return {
      ...state,
      productExcelList: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    ProductActionTypes.getProductsTableConfig, 
    ProductActionTypes.saveProductsTableConfig,
    ProductActionTypes.getProductSiteList,
    (state) => {
    return {
      ...state,
      isLoading: true,
    };
  }),
  on(ProductActionTypes.loadProductsTableConfig, (state, { response }) => {
    return {
      ...state,
      productTableConfig:
        (response as any)?.result?.value?.tableColumnInfo ?? undefined,
    };
  }),
  on(
    ProductActionTypes.getProduct,
    (state, { code }) =>
      ({
        ...state,
        error: null,
        code: code,
        isLoading: true,
        created: false,
      } as ProductState)
  ),
  on(ProductActionTypes.loadProduct, (state, { response }) => {
    return {
      ...state,
      product: (response as any).result ? (response as any).result : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(ProductActionTypes.resetProduct, (state) => {
    return {
      ...state,
      product: null,
      error: null,
    };
  }),
  on(
    ProductActionTypes.createProduct, 
    ProductActionTypes.updateProduct,
    (state) => {
    return {
      ...state,
    };
  }),
  on(ProductActionTypes.resetCreation, (state) => {
    return {
      ...state,
      created: true,
    };
  }),
  on(
    ProductActionTypes.getWarehouseProductsList,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
        isLoading: true,
      } as ProductState)
  ),
  on(ProductActionTypes.loadWarehouseProductsList, (state, { response }) => {
    return {
      ...state,
      warehouseProducts: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(ProductActionTypes.setLoadingEndState, (state) => {
    return {
      ...state,
      isLoading: false,
    };
  }),
  on(
    ProductActionTypes.createProduct,
    ProductActionTypes.updateProduct,
    ProductActionTypes.createSiteList,
    (state) => {
      return {
        ...state,
        isLoading: true,
      };
    }
  ),
  on(ProductActionTypes.loadCategoriesByUsage, (state, { response }) => {
    return {
      ...state,
      categories: (response as any)?.result?.items || [],
      error: null,
    };
  }),
  on(ProductActionTypes.loadPropertiesByUsage, (state, { response }) => {
    return {
      ...state,
      properties: (response as any)?.result?.items || [],
      error: null,
    };
  }),
  on(ProductActionTypes.loadProductSiteList, (state, { response }) => {
    return {
      ...state,
      siteList: (response as any).result
        ? (response as any).result
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(ProductActionTypes.loadBarcodeTypes, (state, { response }) => {
    return {
      ...state,
      barcodeTypes: (response as any).result || [],
      error: null,
    }
  })
);

export function reducer(state: ProductState | undefined, action: Action) {
  return productReducer(state, action);
}
